import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, CheckCircle2 } from 'lucide-react';
import { useRegularProjects } from '../hooks/useProjects';
import { useTickets } from '../hooks/useTickets';
import { useUserStore } from '../store/userStore';
import { DashboardHeader } from '../components/DashboardHeader';
import { useNavigate } from 'react-router-dom';
import type { Project } from '../types';
import { getRiskBandDisplay, formatRiskScore, DEFAULT_ALERT_THRESHOLD } from '../lib/riskBand';
import { confidenceFromScore } from '../lib/deliveryConfidence';

function confidenceLabel(pct: number | null): { text: string; cls: string } {
  // null = not yet evaluated. Unknown must render as unknown — never as
  // "LOW" red, which fabricates a confident negative from missing data.
  if (pct === null) return { text: 'NO SCORE', cls: 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/10' };
  if (pct >= 80) return { text: 'HIGH', cls: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' };
  if (pct >= 60) return { text: 'MEDIUM', cls: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20' };
  return { text: 'LOW', cls: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20' };
}

function ProjectSkeleton() {
  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="skeleton h-4 w-2/3 rounded" />
      <div className="skeleton h-3 w-1/3 rounded" />
    </div>
  );
}

function DeliveryConfidenceList({ projects }: { projects: Project[] }) {
  // Lowest confidence first; unscored projects sort last — "we don't know"
  // does not belong at the top of a least-confident list.
  const sorted = [...projects].sort(
    (a, b) =>
      (confidenceFromScore(a.overallRiskScore) ?? Number.POSITIVE_INFINITY) -
      (confidenceFromScore(b.overallRiskScore) ?? Number.POSITIVE_INFINITY)
  );

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl overflow-hidden">
      {sorted.length === 0 ? (
        <p className="text-body font-medium text-gray-500 text-center py-8">No projects.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
          {sorted.map((project) => {
            const pct = confidenceFromScore(project.overallRiskScore);
            const { text, cls } = confidenceLabel(pct);
            return (
              <li key={project.projectId} className="flex items-center gap-4 px-4 py-3">
                <span className="text-title text-gray-900 dark:text-white truncate flex-1">
                  {project.name}
                </span>
                <span className="text-body font-semibold text-gray-700 dark:text-gray-300 flex-shrink-0">
                  {pct !== null ? `${pct}%` : '—'}
                </span>
                <span className={`text-meta font-bold px-2 py-0.5 rounded flex-shrink-0 ${cls}`}>
                  {text}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AtRiskCustomerImpact({ projects, alertThreshold }: { projects: Project[]; alertThreshold: number }) {
  const atRisk = projects.filter((p) => p.status === 'at_risk');

  if (atRisk.length === 0) {
    return (
      <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5">
        <p className="text-meta text-gray-500">No at-risk projects currently.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl overflow-hidden">
      <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
        {atRisk.map((project) => (
          <li key={project.projectId} className="flex items-center gap-4 px-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="text-title text-gray-900 dark:text-white truncate">
                {project.name}
              </p>
              {project.overallRiskScore !== null && (
                <p
                  title={`Score: ${formatRiskScore(project.overallRiskScore)} / 5.0`}
                  className={`text-meta font-medium cursor-default ${getRiskBandDisplay(project.overallRiskScore, alertThreshold).textClass}`}
                >
                  Risk: {getRiskBandDisplay(project.overallRiskScore, alertThreshold).label}
                </p>
              )}
            </div>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex-shrink-0 text-meta font-medium px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              Get Update
            </motion.button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const REVIEW_LABELS = ['product', 'marketing', 'product-reviewed', 'marketing-reviewed', 'pm-approved', 'mkt-approved'];

function reviewBadges(labels: string[] | null | undefined): string[] {
  if (!labels?.length) return [];
  return labels.filter((l) => REVIEW_LABELS.some((r) => l.toLowerCase().includes(r)));
}

function RecentCompletionsForProject({ project }: { project: Project }) {
  const { data: tickets } = useTickets(project.projectId, { status: 'complete' });

  const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recent = (tickets ?? [])
    .filter((t) => new Date(t.updatedAt).getTime() >= cutoff)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  if (recent.length === 0) return null;

  return (
    <>
      {recent.map((ticket) => {
        const badges = reviewBadges(ticket.labels);
        const hasProductReview = badges.some((b) => b.toLowerCase().includes('product') || b.toLowerCase().includes('pm'));
        const hasMarketingReview = badges.some((b) => b.toLowerCase().includes('market') || b.toLowerCase().includes('mkt'));
        return (
          <div
            key={ticket.ticketId}
            className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06]"
          >
            <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-title text-gray-900 dark:text-white truncate">{ticket.title}</p>
              <p className="text-meta text-gray-500 dark:text-gray-400">{project.name}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {hasProductReview && (
                <span className="text-meta font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                  PM ✓
                </span>
              )}
              {hasMarketingReview && (
                <span className="text-meta font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400">
                  MKT ✓
                </span>
              )}
              {!hasProductReview && !hasMarketingReview && badges.length === 0 && (
                <span className="text-meta text-gray-400 dark:text-gray-500">pending review</span>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

function FeatureStatusList({ projects }: { projects: Project[] }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const statusColors: Record<string, string> = {
    at_risk: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    on_track: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    active: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    planning: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    draft: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    complete: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400',
  };

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-white/[0.06]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects..."
            className="w-full pl-8 pr-3 py-1.5 rounded-lg text-meta bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
          />
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-body font-medium text-gray-500 text-center py-6">No projects match.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
          {filtered.map((project) => {
            const pct = confidenceFromScore(project.overallRiskScore);
            const { text, cls } = confidenceLabel(pct);
            const colorCls = statusColors[project.status] ?? statusColors.archived;
            return (
              <li
                key={project.projectId}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.02] cursor-pointer transition-colors"
                onClick={() => navigate(`/projects/${project.projectId}`)}
              >
                <span className="text-title text-gray-900 dark:text-white truncate flex-1">
                  {project.name}
                </span>
                <span className={`text-meta font-semibold px-2 py-0.5 rounded-full ${colorCls}`}>
                  {project.status?.replace('_', ' ')}
                </span>
                <span className={`text-meta font-bold px-2 py-0.5 rounded ${cls}`}>
                  {text}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function SalesDashboard({ onBackToToday }: { onBackToToday?: () => void } = {}) {
  const { sessionConfig } = useUserStore();
  const alertThreshold = sessionConfig?.alertThreshold ?? DEFAULT_ALERT_THRESHOLD;
  const { data: projects, isLoading } = useRegularProjects();

  const regularProjects = projects ?? [];

  return (
    <div className="px-6 py-6 sm:px-10 lg:px-16">
      <DashboardHeader title="Sales View" roleLabel="Sales" onBackToToday={onBackToToday} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delivery Confidence */}
        <div className="space-y-4">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Delivery Confidence
          </h2>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => <ProjectSkeleton key={i} />)}
            </div>
          ) : (
            <DeliveryConfidenceList projects={regularProjects} />
          )}
        </div>

        {/* At Risk — Customer Impact */}
        <div className="space-y-4">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            At Risk — Customer Impact
          </h2>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => <ProjectSkeleton key={i} />)}
            </div>
          ) : (
            <AtRiskCustomerImpact projects={regularProjects} alertThreshold={alertThreshold} />
          )}
        </div>

        {/* Feature Status */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Feature Status
          </h2>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => <ProjectSkeleton key={i} />)}
            </div>
          ) : (
            <FeatureStatusList projects={regularProjects} />
          )}
        </div>

        {/* Recently Completed — Ready to Go */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-baseline gap-2">
            <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Recently Completed
            </h2>
            <span className="text-meta text-gray-400 dark:text-gray-500">
              last 30 days · PM ✓ and MKT ✓ = ready to go
            </span>
          </div>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => <ProjectSkeleton key={i} />)}
            </div>
          ) : (
            <div className="space-y-4">
              {regularProjects.map((project) => (
                <RecentCompletionsForProject key={project.projectId} project={project} />
              ))}
              {regularProjects.length === 0 && (
                <p className="text-body font-medium text-gray-500 text-center py-6">No projects.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
